import { useEffect, useState } from 'react';
import type { ChoicesResponse } from '../lib/types.js';
import { Icon } from './Icon.js';
import { createTts } from '../lib/tts.js';

type Props = {
  question: ChoicesResponse;
  // onResult fires when the user advances to the next question. For correct
  // picks this auto-fires after a brief reveal; for wrong picks it only fires
  // when the user manually presses ➡️ (so they can study the right answer).
  onResult: (result: { correct: boolean; pickedIndex: number }) => void;
  resetKey: string;
  progress: { mastered: number; total: number };
  // History navigation. When historyView is provided, the card renders a past
  // question with the previous pick already revealed (read-only). canGoBack
  // controls whether ⬅️ is enabled in live mode.
  historyView?: { pickedIndex: number } | null;
  canGoBack?: boolean;
  onBack?: () => void;
};

const REVEAL_CORRECT_MS = 700;

const tts = createTts();

export function QuizCard({
  question,
  onResult,
  resetKey,
  progress,
  historyView,
  canGoBack,
  onBack,
}: Props): JSX.Element {
  const [picked, setPicked] = useState<number | null>(historyView?.pickedIndex ?? null);

  useEffect(() => {
    setPicked(historyView?.pickedIndex ?? null);
    // Auto-play TTS for audio direction on each new live question. Skip in
    // history view so reviewing a past question doesn't surprise-play audio.
    if (!historyView && question.direction === 'audio' && question.audio_word) {
      tts.speak(question.audio_word);
    }
  }, [resetKey, question.direction, question.audio_word, historyView]);

  const isHistory = historyView != null;
  const reveal = picked !== null;
  const pickedCorrect = reveal && picked === question.correct_index;

  const handlePick = (i: number) => {
    if (isHistory || picked !== null) return;
    setPicked(i);
    if (i === question.correct_index) {
      // Correct: brief reveal then auto-advance.
      window.setTimeout(() => onResult({ correct: true, pickedIndex: i }), REVEAL_CORRECT_MS);
    }
    // Wrong: stay on the card so the user can study; ➡️ commits the answer.
  };

  const handleAdvance = () => {
    if (isHistory) {
      onBack?.(); // ➡️ in history view: return to live (LearnPhase decides)
      return;
    }
    if (picked === null) return;
    onResult({ correct: picked === question.correct_index, pickedIndex: picked });
  };

  const pctRaw = progress.total > 0 ? (progress.mastered / progress.total) * 100 : 0;
  const pct = Math.max(0, Math.min(100, pctRaw));

  // ➡️ is shown after the user has picked. For correct picks it briefly races
  // with the auto-advance timer; clicking it just advances slightly faster.
  // For wrong picks it is the only way forward.
  const advanceLabel = isHistory ? '回到当前' : '下一题';
  const showAdvance = reveal;
  // ⬅️ in live mode: enter history (if available). In history mode: ⬅️ is the
  // same as showing the next slot in the (in-memory) history — currently we
  // only keep one history entry, so this stays disabled there.
  const showBack = !isHistory && Boolean(canGoBack);

  return (
    <div className="w-full max-w-card flex flex-col items-center gap-3 pb-28">
      <div className="w-full">
        <div className="flex items-center justify-between mb-1.5 text-secondary text-hint">
          <span>
            {progress.mastered} / {progress.total}
          </span>
          {isHistory ? <span>上一题（已答）</span> : null}
        </div>
        <div className="h-1 w-full bg-surface-2 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <QuestionPanel question={question} disableSpeak={isHistory} revealed={reveal} />

      <div className="w-full grid grid-cols-1 gap-2">
        {question.options.map((opt, i) => {
          const isPicked = picked === i;
          const isCorrect = i === question.correct_index;
          const showCorrect = reveal && isCorrect;
          const showWrong = reveal && isPicked && !isCorrect;
          const cls = [
            'w-full px-4 py-3 rounded-xl border text-sentence transition-colors text-left flex items-center justify-between gap-3',
            showCorrect
              ? 'bg-primary text-canvas border-primary'
              : showWrong
                ? 'bg-surface-2 text-primary border-border line-through'
                : 'bg-surface text-primary border-border active:bg-surface-2',
          ].join(' ');
          return (
            <button
              key={i}
              type="button"
              className={cls}
              onClick={() => handlePick(i)}
              disabled={reveal || isHistory}
            >
              <span className="flex-1">{opt}</span>
              {showCorrect ? (
                <span aria-hidden>✅</span>
              ) : showWrong ? (
                <span aria-hidden>❌</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <NavBar
        showAdvance={showAdvance}
        advanceLabel={advanceLabel}
        showBack={showBack}
        onAdvance={handleAdvance}
        onBack={onBack}
      />
    </div>
  );
}

// On reveal we extend the question panel itself with whatever the direction
// was hiding, instead of stacking a separate panel below the options. Keeps
// the layout compact so the fixed bottom nav bar never overlaps content.
//
// Coverage matrix (what the question + correct option already show vs. what
// needs to be appended on reveal):
//   en2zh  word + phonetic + zh → nothing to append
//   zh2en  zh + word            → append phonetic
//   audio  zh                   → append word + phonetic
//   cloze  blanked-sentence + word → append phonetic + zh
function QuestionPanel({
  question,
  disableSpeak,
  revealed,
}: {
  question: ChoicesResponse;
  disableSpeak: boolean;
  revealed: boolean;
}): JSX.Element {
  const dir = question.direction;
  if (dir === 'audio') {
    // Compact layout: mic + tap-to-replay hint inline; on reveal, append the
    // word + phonetic right under, no divider line. Keeps the panel short
    // enough that all 4 options remain visible above the fixed bottom nav.
    return (
      <div className="w-full bg-surface border border-border rounded-2xl px-6 py-5 flex flex-col items-center gap-2">
        <button
          type="button"
          aria-label="重播"
          onClick={() => !disableSpeak && question.audio_word && tts.speak(question.audio_word)}
          disabled={disableSpeak}
          className="h-12 w-12 rounded-full border border-border flex items-center justify-center text-primary active:bg-surface-2 disabled:opacity-50"
        >
          <Icon name="mic" size={22} />
        </button>
        <div className="text-secondary text-hint">点击重播</div>
        {revealed ? (
          <>
            <div className="text-word-lg text-primary mt-1">{question.word}</div>
            {question.phonetic ? (
              <div className="text-ipa text-secondary">/{question.phonetic}/</div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }
  if (dir === 'cloze') {
    return (
      <div className="w-full bg-surface border border-border rounded-2xl px-6 py-5">
        <div className="text-secondary text-hint mb-1.5">选填入空缺的词</div>
        <div className="text-sentence text-primary leading-snug">{question.question}</div>
        {revealed ? (
          <div className="mt-2 pt-2 border-t border-border flex flex-col items-center gap-0.5">
            {question.phonetic ? (
              <div className="text-ipa text-secondary">/{question.phonetic}/</div>
            ) : null}
            <div className="text-gloss text-primary text-center">{question.translation_zh}</div>
          </div>
        ) : null}
      </div>
    );
  }
  // en2zh / zh2en
  return (
    <div className="w-full bg-surface border border-border rounded-2xl px-6 py-5 text-center flex flex-col items-center gap-1">
      <div className="text-secondary text-hint">
        {dir === 'en2zh' ? '请选择中文释义' : '请选择对应英文'}
      </div>
      <div className="text-word-lg text-primary break-words">{question.question}</div>
      {dir === 'en2zh' && question.phonetic ? (
        <div className="text-ipa text-secondary">/{question.phonetic}/</div>
      ) : null}
      {revealed && dir === 'zh2en' && question.phonetic ? (
        <div className="text-ipa text-secondary mt-0.5">/{question.phonetic}/</div>
      ) : null}
    </div>
  );
}

function NavBar({
  showAdvance,
  advanceLabel,
  showBack,
  onAdvance,
  onBack,
}: {
  showAdvance: boolean;
  advanceLabel: string;
  showBack: boolean;
  onAdvance: () => void;
  onBack?: () => void;
}): JSX.Element {
  // Pinned to the viewport bottom so it stays visible on phones where the
  // reveal panel + 4 options + question often push past the fold. Both arrows
  // always render so the layout stays stable; we just disable them when the
  // action isn't currently meaningful. The pb-24 spacer in QuizCard keeps page
  // content from sliding under this bar.
  return (
    <div className="fixed left-0 right-0 bottom-0 z-30 px-4 pt-2 pb-2 bg-canvas/95 backdrop-blur border-t border-border">
      <div className="w-full max-w-card mx-auto flex items-center justify-between">
        <button
          type="button"
          aria-label="上一题"
          onClick={() => showBack && onBack?.()}
          disabled={!showBack}
          className="h-12 w-12 rounded-full border border-border flex items-center justify-center text-primary active:bg-surface-2 disabled:opacity-30 disabled:active:bg-transparent"
        >
          <ArrowIcon dir="left" />
        </button>
        <button
          type="button"
          aria-label={advanceLabel}
          onClick={() => showAdvance && onAdvance()}
          disabled={!showAdvance}
          className="h-12 px-5 rounded-full border border-primary bg-primary text-canvas flex items-center gap-2 active:bg-primary/80 disabled:opacity-30 disabled:bg-surface-2 disabled:text-secondary disabled:border-border disabled:active:bg-surface-2"
        >
          <span className="text-sentence">{advanceLabel}</span>
          <ArrowIcon dir="right" />
        </button>
      </div>
    </div>
  );
}

function ArrowIcon({ dir }: { dir: 'left' | 'right' }): JSX.Element {
  // Inline arrow — keeps the Icon module focused on the core set we use
  // elsewhere. ←/→ are only needed by this component.
  const d = dir === 'left' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={20}
      height={20}
      aria-hidden="true"
    >
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
